/**
 * Human-readable record labels for audit entries.
 *
 * Assignment rows are most useful when they say WHO was assigned and in what
 * role, not just the project name. e.g. "CAP · Nathan Papes · Developer".
 */

import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { isArchivedProject } from "@/utils/projectUtils";

interface AssignmentLike {
  employees?: { name: string | null; nickname?: string | null }[] | null;
  required_role_id?: string | null;
}

/**
 * Resolves a role id to its display name. Roles are now configurable (stored in
 * the `roles` table), so callers pass `useRoles().formatRole` to turn the
 * assignment's `required_role_id` into a human label.
 */
type FormatRole = (id: string | null | undefined) => string;

interface ProjectLike {
  name: string;
  deal_stage?: { kind: string | null } | null;
  start_date: string;
  num_weeks: number;
}

/**
 * Label for a project in audit entries. Archived projects are flagged so the
 * audit log makes it obvious that an archived project was edited.
 */
export function projectAuditLabel(project: ProjectLike): string {
  return isArchivedProject(project) ? `${project.name} (ARCHIVED)` : project.name;
}

export function assignmentAuditLabel(
  projectName: string,
  assignment: AssignmentLike,
  formatRole: FormatRole,
): string {
  const names = (assignment.employees ?? [])
    .map((employee) => formatEmployeeDisplayName(employee))
    .filter(Boolean)
    .join(" / ");
  const role = formatRole(assignment.required_role_id) || null;
  const who = names || "Unassigned";
  return role ? `${projectName} · ${who} · ${role}` : `${projectName} · ${who}`;
}
